import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { IndicationDefinition, IndicationGroup } from '@app/models/indication';
import { InputOption } from '@app/models/input-option';
import { SelectionsModel } from '@app/models/selections-model';
import { SharedService } from '@app/services/shared.service';
import { LimitsInputComponent } from '@app/shared/inputs/limits-input/limits-input.component';
import { RadioInputGroupComponent } from '@app/shared/inputs/radio-input-group/radio-input-group.component';
import { vennHealthConstants } from '@app/shared/vennHealthConstants';
import { VennHealthHelpers } from '@app/shared/vennHealthHelpers';
import { asapScheduler, first, skip } from 'rxjs';

@Component({
  selector: 'app-asthma',
  templateUrl: './asthma.component.html',
  styleUrls: ['./asthma.component.css']
})
export class AsthmaComponent implements OnInit {

  @ViewChild('childCriteriaComponent') childCriteriaComponent: RadioInputGroupComponent;
  @ViewChild('childLowerLimitsComponent') childLowerLimitsComponent: LimitsInputComponent;

  @Input() indication: IndicationDefinition;
  criteriaIndication: IndicationDefinition;
  lowerLimitsIndication: IndicationDefinition;
  
  
  allIndications: IndicationGroup[] = [];

  childCriteriaSelectionModel: SelectionsModel;
  childLimitsSelectionModel: SelectionsModel[];

  lowerLimitsDefault = 0.3;

  asthmaInputOptions: InputOption[][] = [
    // criteria inputs
    [
      {name: 'current', displayName: 'Current', order: 1, defaultValue: 'current'},
      {name: 'lifetime', displayName: 'Lifetime', order: 2, defaultValue: 'current'}
    ],
    // lower limits input
    [
      {name: 'lowerLimits', displayName: 'Eosinophils number Lower Limits', order: 1, defaultValue: this.lowerLimitsDefault, maxlength: 5},
    ]
  ];

  constructor(
    private sharedService: SharedService
  ) { }

  ngOnInit() {

    this.criteriaIndication = Object.assign({}, this.indication);
    this.criteriaIndication.inputOptions = this.asthmaInputOptions[0];

    this.lowerLimitsIndication = Object.assign({}, this.indication);
    this.lowerLimitsIndication.units = '1000 cell/uL';
    this.lowerLimitsIndication.displayName = 'Eosinophils number';
    this.lowerLimitsIndication.inputOptions = this.asthmaInputOptions[1];

    // only called first time to setup form and update selected filters via shared service
    this.sharedService.allIndications.pipe(first()).subscribe(data => {
      if (data) {
        this.allIndications = data;
        this.setIndicationSelections();
      }
    });
    // called everytime after the first time
    this.sharedService.allIndications.pipe(skip(1)).subscribe(data => {
      if (data) {
        this.allIndications = data;
      }
    });

    this.sharedService.activeSavedFilters.subscribe(data => {
      if (data) {
        const loadedIndication = VennHealthHelpers.getCurrentIndicationFromIndicationGroupJson(this.indication, this.allIndications);
        this.indication.selections = loadedIndication.selections;
        this.indication.selected = loadedIndication.selected;

        this.setIndicationSelections();
      }
    });
  }

  setIndicationSelections() {
    if (this.indication.selected && this.indication.selections.length > 0) {
      asapScheduler.schedule(() => {
        if (this.indication.selections.some(i => i.controlName === 'criteria')) {
          this.childCriteriaComponent.overrideInputValue(this.indication.selections.find(i => i.controlName === 'criteria').selectionValue);
        }
        if (this.childLowerLimitsComponent && this.indication.selections.some(i => i.controlName === 'lowerLimits')) {
          this.childLowerLimitsComponent.overrideInputValue('lowerLimits', this.indication.selections.find(i => i.controlName === 'lowerLimits').selectionValue);
        }
        this.updateSelections();
      });
    } else {
      this.setDefaults();
    }
  }

  childSharedRadioControlSelectionChangedHandler(selectionModel: SelectionsModel) {
    this.childCriteriaSelectionModel = selectionModel;
    this.updateSelections();
  }

  childSharedTextControlSelectionChangedHandler(selections: SelectionsModel[]) {
    this.childLimitsSelectionModel = selections;
    this.updateSelections();
  }

  setDefaults() {
    asapScheduler.schedule(() => {
      this.childCriteriaComponent.overrideInputValue('current');
      this.setLimitsDefaults();
      this.updateSelections();
    });
  }

  setLimitsDefaults() {
    if (this.childLowerLimitsComponent) {
      this.childLowerLimitsComponent.overrideInputValue('lowerLimits', this.lowerLimitsDefault);
    }
  }

  updateSelections(): void {
    let currentSelections: SelectionsModel[] = [];

    if (this.childCriteriaSelectionModel) {
      currentSelections.push(this.childCriteriaSelectionModel);
    }

    if (this.childLimitsSelectionModel) {
      currentSelections = currentSelections.concat(this.childLimitsSelectionModel);
    }
    
    this.indication.selections = currentSelections;
    const currentIndicationGroup = VennHealthHelpers.getIndicationGroup(this.indication);
    const matchingGroupIndex = this.allIndications.findIndex(i => i.name === currentIndicationGroup.name);
    const matchingIndicationIndex = this.allIndications[matchingGroupIndex].indications.findIndex(i => i.name === this.indication.name);
    this.allIndications[matchingGroupIndex].indications[matchingIndicationIndex] = this.indication;
    this.sharedService.setAllIndications(this.allIndications);
  }
}
